import React from 'react';
import moment from 'moment';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import {
  showPrice,
  unitFormatter,
  getCollectionAndDeliveryFee,
  getCollectionAndDeliveryFeeText,
  getOrderTotal,
  showPartialPaymentRecord,
  capitalizeStr,
  getOrderDiscountTotal,
  inWords
} from 'common/utilFunctions';
import MainLogo from '../../assets/png/Launder-it-logo.png';

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    backgroundColor: '#E4E4E4'
  },
  section: {
    fontSize: '14px',
    padding: 10,
    lineHeight: 1.5
  },
  logoContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 5
  },
  logo: {
    width: 150
  },
  tableHeaderContainer: {
    backgroundColor: '#bff0fd',
    textAlign: 'center',
    flexDirection: 'row',
    padding: 10,
    justifyContent: 'space-between'
  },
  tableHeader: {
    fontSize: '12px',
    width: '25%'
  },
  leftAlign: {
    textAlign: 'left'
  },
  rightAlign: {
    textAlign: 'right'
  },
  centerAlign: {
    textAlign: 'center'
  },
  tableContent: {
    fontSize: '12px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginLeft: 10,
    marginRight: 10,
    marginTop: 10,
    marginBottom: 10,
    borderBottom: 1,
    borderBottomColor: '#bff0fd'
  },
  paymentContent: {
    fontSize: '10px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginLeft: 2,
    marginRight: 8,
    marginTop: 10,
    marginBottom: 10,
    borderBottom: 1,
    borderBottomColor: '#bff0fd'
  },
  tableCell: {
    marginLeft: 10
  },
  tableTotalContainer: {
    width: '50%',
    marginLeft: '50%'
  },
  tableTotalContent: {
    fontSize: '12px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10
  },
  totalPrice: {
    fontSize: '13px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10,
    backgroundColor: '#bff0fd'
  },
  tableCellWidth: {
    width: '20%'
  },
  paymentStatus: {
    textAlign: 'center',
    fontSize: '12px',
    marginTop: '15px'
  },
  discount: {
    fontSize: 7,
    padding: '5px 0px',
    textAlign: 'center'
  }
});

const OrderTable = ({ orderList, total, order, payment }) => {
  let balance = total;

  return (
    <View>
      <View style={styles.tableHeaderContainer}>
        <Text style={[styles.tableHeader, styles.leftAlign]}>ITEM</Text>
        <Text style={[styles.tableHeader, styles.centerAlign]}>QUANTITY</Text>
        <Text style={[styles.tableHeader, styles.centerAlign]}>PRICE</Text>
        <Text style={[styles.tableHeader, styles.centerAlign]}>DISCOUNT</Text>
        <Text style={[styles.tableHeader, styles.rightAlign]}>TOTAL</Text>
      </View>
      {orderList.length > 0 &&
        orderList.map((item, index) => (
          <View
            style={styles.tableContent}
            key={`order-document-pdf-order-items-${index}-${item.productID}`}>
            <Text
              style={[
                styles.tableCellWidth,
                styles.leftAlign
              ]}>{`${item.name}`}</Text>
            <Text
              style={[
                styles.tableCellWidth,
                styles.centerAlign
              ]}>{`${item.quantity}`}</Text>
            <Text style={[styles.tableCellWidth, styles.centerAlign]}>{`${
              item.price
            } ${unitFormatter(item.unit)}`}</Text>
            <Text style={[styles.tableCellWidth, styles.centerAlign]}>
              {item.discount
                ? `${showPrice(item.discount || 0)} ${unitFormatter(
                    item.unit
                  )}`.toLowerCase()
                : item.overallDiscount
                ? `${showPrice(item.overallDiscount)}`
                : 0}
            </Text>
            <Text style={[styles.tableCellWidth, styles.rightAlign]}>
              {showPrice(
                item.price * item.quantity - (item.overallDiscount || 0)
              )}
            </Text>
          </View>
        ))}
      <View style={styles.tableTotalContainer}>
        <View style={styles.tableTotalContent}>
          <Text>Subtotal</Text>
          <Text>{showPrice(getOrderTotal(order))}</Text>
        </View>
        <View style={styles.tableTotalContent}>
          <Text>Tax: </Text>
          <Text>{showPrice(0)}</Text>
        </View>
        {order?.collection?.type === 'asap' ? (
          <View style={styles.tableTotalContent}>
            <Text>ASAP Fee</Text>
            <Text>{showPrice(order.shop?.AsapDeliveryFee)}</Text>
          </View>
        ) : (
          ''
        )}
        {order?.collectionAndDeliveryFee > 0 ? (
          <View style={styles.tableTotalContent}>
            <Text>{getCollectionAndDeliveryFeeText(order)}</Text>
            <Text>{getCollectionAndDeliveryFee(order)}</Text>
          </View>
        ) : (
          ''
        )}
        <View style={styles.totalPrice}>
          <Text>Total</Text>
          <Text>{showPrice(total ? total : 0)}</Text>
        </View>
        {getOrderDiscountTotal(order) ? (
          <View style={styles.discount}>
            <Text>
              <strong>Note:-</strong> Direct Discount:{'  '}
              {showPrice(
                getOrderDiscountTotal({
                  orderList:
                    order.orderList?.filter((item) =>
                      item?.discountCoupon
                        ? JSON.parse(item?.discountCoupon).discountType ===
                          'direct'
                        : false
                    ) || []
                })
              )}
              , Discount from Coupons:{'  '}
              {showPrice(
                getOrderDiscountTotal({
                  orderList:
                    order.orderList?.filter((item) =>
                      item?.discountCoupon
                        ? JSON.parse(item?.discountCoupon).discountType ===
                          'conditional'
                        : false
                    ) || []
                })
              )}
              , Overall Discount: {'  '}
              {showPrice(getOrderDiscountTotal(order))}
            </Text>
          </View>
        ) : null}
        {payment?.length > 0 &&
          (showPartialPaymentRecord(payment, total) ? (
            <>
              {payment.map((el, i) => {
                balance -= el.amount;

                return (
                  <View
                    key={`partialPaymentRecordsItems-${i}`}
                    style={styles.paymentContent}>
                    <View colSpan={2}>
                      <Text>
                        Partial Cash Payment on :&nbsp;
                        {moment(el.date).format('DD/MM/YYYY, h:mm a')}
                        &ensp;
                      </Text>
                      <br />
                      <Text>
                        {balance <= 0 ? 'Total Amount Due:' : 'Balance Due:'}
                      </Text>
                    </View>
                    <View>
                      <Text align="right">{showPrice(el.amount)}</Text>
                      <br />
                      <Text>{showPrice(balance)}</Text>
                    </View>
                  </View>
                );
              })}
            </>
          ) : (
            <>
              <View style={styles.paymentContent}>
                <View colSpan={2}>
                  <Text>
                    Cash Payment on :&nbsp;
                    {moment(payment[0].date).format('DD/MM/YYYY, h:mm a')}
                    &ensp;
                  </Text>
                </View>
                <View>
                  <Text align="right">{showPrice(payment[0].amount)}</Text>
                </View>
              </View>
            </>
          ))}
      </View>
    </View>
  );
};

// PDF Document Component
const OrderDocumentPDF = ({ order, payment }) => {
  return (
    <Document>
      <Page size="A4">
        <View style={styles.logoContainer}>
          <Image src={MainLogo} style={styles.logo} />
        </View>
        <View>
          <Text
            style={{
              textAlign: 'center',
              fontSize: '16px',
              marginTop: 15,
              backgroundColor: '#bff0fd',
              padding: 10
            }}>
            {order?.shop?.name.toUpperCase()}
          </Text>
          <Text
            style={{
              textAlign: 'center',
              fontSize: '11px',
              marginBottom: 15,
              marginTop: '5px',
              padding: 2
            }}>
            {order?.shop?.address}.
          </Text>
        </View>

        <View style={styles.section}>
          <View>
            <Text style={{ fontSize: '15px' }}>{order?.orderID}</Text>
            <Text style={{ fontSize: '12px' }}>
              Ordered on
              {moment(order?.createdAt).format(' MMMM Do YYYY')}
            </Text>
            <Text
              style={{
                fontSize: '12px'
              }}>
              {order?.collection?.date &&
                `Collection date: ${moment(order?.collection?.date).format(
                  ' MMMM Do YYYY'
                )}`}
            </Text>
            <Text
              style={{
                fontSize: '12px'
              }}>
              {order?.delivery?.date &&
                `Delivery date: ${moment(order?.delivery?.date).format(
                  ' MMMM Do YYYY'
                )}`}
            </Text>
            <View>
              <Text style={{ fontSize: '12px' }}>
                Contact Name:
                {`${order?.userDetail?.title || ''} ${
                  order?.userDetail?.firstName || ''
                } ${order?.userDetail?.lastName || ''}`}
              </Text>
              <Text style={{ fontSize: '12px' }}>
                {`Email address: ${order?.userDetail?.email}`}
              </Text>
              <Text style={{ fontSize: '12px' }}>
                Phone number: {order?.userDetail?.phoneNumber}
              </Text>
            </View>
          </View>
          <View>
            {order?.collection?.address && (
              <View>
                <Text style={{ fontSize: '12px' }}>
                  Collection Address:{' '}
                  {JSON.parse(order?.collection?.address)['line1']}
                  {','}
                  {JSON.parse(order?.collection?.address)['town']}
                </Text>
              </View>
            )}
            {order?.delivery?.address && (
              <View>
                <Text style={{ fontSize: '12px' }}>
                  Delivery Address:{' '}
                  {JSON.parse(order?.delivery?.address)['line1']}
                  {','}
                  {JSON.parse(order?.delivery?.address)['town']}
                </Text>
              </View>
            )}
            {order?.collection?.type === 'asap' && (
              <Text
                style={{
                  textAlign: 'center',
                  backgroundColor: '#e53935e3',
                  color: '#ffff',
                  marginTop: '5px',
                  borderRadius: '2px',
                  padding: '2px'
                }}>
                This is an ASAP Order
              </Text>
            )}
          </View>
        </View>

        <View>
          <OrderTable
            orderList={order?.orderList || []}
            total={order?.total || 0}
            order={order}
            payment={payment}
          />
        </View>

        {order?.paymentStatus === 'paid' && (
          <View>
            <Text style={styles.paymentStatus}>
              A Cash Payment of {capitalizeStr(inWords(order?.total))} is Made.
            </Text>
          </View>
        )}
        {order?.paymentStatus === 'partiallyPaid' && (
          <View>
            <Text style={styles.paymentStatus}>
              Payment of {showPrice(order?.paidAmount)} out of{' '}
              {showPrice(order?.total)} was Paid
            </Text>
          </View>
        )}
        {order?.paymentStatus === 'unpaid' && (
          <View>
            <Text style={styles.paymentStatus}>Payment unpaid</Text>
          </View>
        )}

        <View>
          <Text
            style={{
              textAlign: 'center',
              fontSize: '15px',
              marginTop: '20px'
            }}>
            Thank You for choosing {order?.shop?.name}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default OrderDocumentPDF;
